/**
 * @module app.RouteLogoService
 */
import appBase from './index.js';
import {declutterRouteLogo} from './hiddenfeature.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleIcon from 'ol/style/Icon.js';
import {MAP_PROJECTION, ROUTE_SCOPE, STAGES, Z_INDEX} from "./constants";
import {isEmpty} from "ol/obj";

/**
 * The array of lands in the same order as in the layertree.
 * Respecting the order is necessary to have correct ordering of the route logo layers.
 * See https://jira.camptocamp.com/browse/GSCHM-850.
 * @const
 * @type {!Array.<appx.LandURL>}
 */
export const LAND_URLS = [
  {
    land: 'Wanderland',
    filename: 'logos_wander_segment.geojson'
  },
  {
    land: 'Veloland',
    filename: 'logos_velo_segment.geojson'
  },
  {
    land: 'Mtbland',
    filename: 'logos_mountainbike_segment.geojson'
  },
  {
    land: 'Skatingland',
    filename: 'logos_skating_segment.geojson'
  },
  {
    land: 'Kanuland',
    filename: 'logos_kanu_segment.geojson'
  },
  {
    land: 'Winterhiking',
    filename: 'logos_winterhiking_segment.geojson'
  },
  {
    land: 'Snowshoe',
    filename: 'logos_snowshoe_segment.geojson'
  },
  {
    land: 'Crosscountry',
    filename: 'logos_crosscountry_segment.geojson'
  },
  {
    land: 'Sledging',
    filename: 'logos_sledging_segment.geojson'
  }
].reverse();

class RouteLogoService {
  /**
   * @param {Array.<number>} resolutions
   * @param {string} routeLogosDataUrl
   * @param {app.MapService} appMapService
   * @ngInject
   */
  constructor(resolutions, routeLogosDataUrl, appMapService) {
    /**
     * @type {Array<number>}
     * @private
     */
    this.resolutions_ = resolutions;

    /**
     * @type {string}
     * @private
     */
    this.routeLogosDataUrl_ = routeLogosDataUrl;

    /**
     * @type {app.MapService}
     * @private
     */
    this.appMapService_ = appMapService;

    /**
     * @type {!Object<string, ol.layer.Vector>}
     * @private
     */
    this.logoLayers_ = {};

    /**
     * @type {Object.<string, Object.<number, ol.style.Style>>}
     * @private
     */
    this.styleCache_ = {};

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.format_ = new olFormatGeoJSON({
      dataProjection: MAP_PROJECTION
    });

    this.declutter_ = declutterRouteLogo();
  }

  /**
   * @param {Array.<number>} resolution
   * @return {number}
   * @private
   */
  normalizeResolution_(resolution) {
    for (let i = this.resolutions_.length - 1; i >= 0; i--) {
      if (resolution <= this.resolutions_[i]) {
        return this.resolutions_[i];
      }
    }
    return this.resolutions_[this.resolutions_.length - 1];
  }

  /**
   * @param {string} logo
   * @param {number} resolution
   * @return {ol.style.Style}
   * @private
   */
  getOrCreateStyleForLogo_(logo, resolution) {
    this.styleCache_[logo] = this.styleCache_[logo] || {};
    let style = this.styleCache_[logo][resolution];
    if (!style) {
      const sizeOriginal = 140;
      const sizeBigResolution = 20;
      const sizeSmallResolution = 28;
      const isSmallResolution = (resolution <= 250);
      // Here we scale down the 140px to either 28px for all resolutions
      // including and below 250 or to 20px for the two largest resolutions.
      // If you change the value here, also change the value in getLogoRadius()
      // in api/route_logos/generate-logos.js.
      const sizeScaled = isSmallResolution ? sizeSmallResolution : sizeBigResolution;
      style = this.styleCache_[logo][resolution] = new olStyleStyle({
        image: new olStyleIcon(/** @type {olx.style.IconOptions} */ ({
          scale: sizeScaled / sizeOriginal,
          // Original size is required for correct scale in pdf generation
          size: [sizeOriginal, sizeOriginal],
          src: logo
        }))
      });
    }
    return style;
  }


  /**
   * @param {Object.<string, boolean>} WMSLayerState
   * @return {Object<string, ol.layer.Vector>}
   */
  getRouteLogoLayers(WMSLayerState) {
    if (isEmpty(this.logoLayers_)) {
      for (const item of LAND_URLS) {
        const land = item.land;
        const filename = item.filename;
        const source = new olSourceVector({
          format: this.format_,
          url: this.routeLogosDataUrl_ + filename
        });

        let initialized = false;
        const categoryByRoute = {};

        const isActive = (routeScope) => {
          let layerName;
          if (routeScope) {
            layerName = `${land}${STAGES}${routeScope}`;
          } else {
            layerName = land;
          }
          return !!WMSLayerState[layerName];
        };

        const allowedCategory = (category, res) => {
          console.assert(category && res);
          // winter layers do not have different scopes, they are treated as local
          const isActiveWinterLayer = isActive();

          const routeScope = ROUTE_SCOPE;
          const national = category === 'national' && isActive(routeScope.NATIONAL);
          const regional = category === 'regional' && isActive(routeScope.REGIONAL);
          const local = category === 'local' && (isActive(routeScope.LOCAL) || isActiveWinterLayer);
          const handicap = category === 'handicap' && isActive(routeScope.HANDICAP);

          // If you change this part, you must change also the values in api/route_logos/generate-logos.js
          if (res <= 20) {
            return national || regional || local || handicap;
          } else if (res <= 100) {
            return national || regional;
          } else {
            return national;
          }
        };

        const getLogoUrlToDisplay = (properties, resolution) => {
          resolution = this.normalizeResolution_(resolution);
          const displayable = properties['resolutions'][resolution];
          // If alternates is defined at this resolution, then there is potentially something to display
          if (displayable) {
            const defaultRoute = properties['route'];
            const defaultUrl = properties['logo'];
            // If the route of the dot can be displayed, then use it
            if (allowedCategory(categoryByRoute[defaultRoute], resolution)) {
              return defaultUrl;
            }
          }

          // There is no match.
          return null;
        };

        const initializeMappings = () => {
          if (!initialized) {
            source.getFeatures().forEach(feature => {
              const route = feature.get('route');
              const category = feature.get('category');
              categoryByRoute[route] = category;
            });
            initialized = true;
          }
        };

        const emptyStyle = Object.seal([]); // no display, no print
        const getStyleFromLogoUrl = (logoUrl, resolution) => {
          if (logoUrl) {
            return this.getOrCreateStyleForLogo_(logoUrl, resolution);
          } else {
            return emptyStyle;
          }
        };

        const styleFunction = (feature, resolution) => {
          initializeMappings();
          const logoUrl = getLogoUrlToDisplay(feature.getProperties(), resolution);
          return getStyleFromLogoUrl(logoUrl, resolution);
        };

        const layer = new olLayerVector({
          'name': `route_logo_${land}`,
          'cesiumIgnore': true,
          zIndex: Z_INDEX.ROUTE_LOGOS,
          visible: false,
          style: styleFunction,
          source: source,
          declutter: this.declutter_
        });

        this.appMapService_.map.addLayer(layer);
        this.logoLayers_[land] = layer;
      }
    }

    return this.logoLayers_;
  }
}

export default appBase.service('appRouteLogoService', RouteLogoService);
